import { useAuthContext } from 'contexts/AuthContext';
import HooksPoster from 'data/HooksPoster';
import type { Questionnaire } from 'fhir/r4';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { Loader, Dropdown, Button, Icon, Modal, Message, Form } from 'semantic-ui-react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import { QuestionnaireExtensionHelper, QuestionnaireHelper } from 'utils/Helpers';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import { isAddendumQuestionnaire } from './fhir-helpers';
import type { QuestionnaireOption, QuestionnairesListProps } from './types';

export default function QuestionnairesList(props: QuestionnairesListProps) {
  enum messageStatus {
    success,
    warning,
    error,
    idle,
  }
  const authInfo = useAuthContext();
  let webSurveyMedium: string = '';
  // `| ''` because the send handler resets it to the empty string, which `webClick`
  // tests for as its "nothing chosen" sentinel. Left uninitialised, as before — see the
  // defect marker at that call.
  let webSurvey: Partial<Questionnaire> | '' | undefined;

  const [launchStatus, setLaunchStatus] = useState(messageStatus.idle);
  const [errorMessage, setErrorMessage] = useState('Oops! Something went wrong..');
  // All three hold `QuestionnaireOption`, not `types`' `Option`: they are built
  // from `Questionnaire.id`/`title`, which FHIR leaves optional.
  const [questionnaireTypesCB, setQuestionnaireTypesCB] = useState<QuestionnaireOption[]>([]); //CareBetty surveys
  const [questionnaireTypesMA, setQuestionnaireTypesMA] = useState<QuestionnaireOption[]>([]); //Member App surveys
  const [_questionnaireTypesText, setQuestionnaireTypesText] = useState<QuestionnaireOption[]>([]); //Text-based surveys
  const [openWeb, setOpenWeb] = useState(false);
  const webSurveyOptions = [
    { key: 'text', text: 'Text', value: 'text' },
    { key: 'email', text: 'Email', value: 'email' },
  ];
  const currentEnv = import.meta.env.VITE_CARE_LYTICS_API_ENDPOINT!.split('/').pop();

  useEffect(() => {
    /*
     * SUSPECTED DEFECT — the `workflow` filter below assumes a `useContext`
     * shape that FHIR does not guarantee, and would throw where the sibling
     * `program` filter does not.
     *
     * Both loops walk `Questionnaire.useContext`. The `program` arm reaches the
     * value through lodash `get(...)`, which returns `undefined` for a missing
     * path. The `workflow` arm reaches it by direct member access —
     * `useContext[i].valueCodeableConcept.text.includes('PRACTITIONER')` — and
     * `valueCodeableConcept` is `0..1` on `UsageContext` while
     * `CodeableConcept.text` is `0..1` as well.
     *
     * So a Questionnaire carrying a `workflow` usage-context whose value is
     * expressed as any of the other `value[x]` choices (`valueQuantity`,
     * `valueRange`, `valueReference`), or as a coding with no `text`, throws
     * `TypeError: Cannot read properties of undefined` inside this `useEffect`
     * and takes down the survey toolbar. The `program` arm on the identical
     * data merely fails to match.
     *
     * LIVE for such a resource, though every Questionnaire seeded today uses
     * `valueCodeableConcept.text`, which is why it has not fired.
     *
     * Not fixed. Adding the optional chaining that would make the two arms agree
     * converts a crash into a silent non-match — a real behaviour change, and
     * the choice between "skip it" and "surface it" is a product decision.
     */
    // limit surveys by org
    const orgSurveys = props.questionnaireData.entry.filter((questionnaire: ResourceEntry<'Questionnaire'>) => {
      if (questionnaire.resource.useContext) {
        let orgMatch = false;
        for (let i = 0; i < questionnaire.resource.useContext.length; i++) {
          if (questionnaire.resource.useContext[i]!.code.code === 'program') {
            if (
              get(questionnaire, `resource.useContext[${i}].valueCodeableConcept.text`) === props.org ||
              get(questionnaire, `resource.useContext[${i}].valueCodeableConcept.text`)?.toUpperCase() === 'ALL'
            ) {
              orgMatch = true;
            } else {
              //orgMatch = false; Keep set to false, do nothing
            }
          }
        }
        return orgMatch;
      } else {
        return true;
      }
    });

    // limit surveys by workflow - PRACTITIONER - includes ones w/o workflow
    const surveysCB = orgSurveys.filter((questionnaire: ResourceEntry<'Questionnaire'>) => {
      if (questionnaire.resource.useContext) {
        let orgMatch = false;
        for (let i = 0; i < questionnaire.resource.useContext.length; i++) {
          if (questionnaire.resource.useContext[i]!.code.code === 'workflow') {
            if (questionnaire.resource.useContext[i]!.valueCodeableConcept!.text!.includes('PRACTITIONER')) {
              orgMatch = true;
            } // else return false;
          }
        }
        return orgMatch;
      } else {
        return true;
      }
    });

    //all surveys with inactive and active status used for survey dropdown filter
    const typesCB = surveysCB.map((option: ResourceEntry<'Questionnaire'>) => {
      const QHelper = new QuestionnaireHelper(option.resource);
      const QExtHelper = new QuestionnaireExtensionHelper(QHelper.getSurveyExtension());
      return {
        key: option.resource.id,
        text: QExtHelper.getInternalTitle() ?? option.resource.title,
        value: option.resource.title,
      };
    });

    //disable the pii survey selection
    const addTypesCBPIIIdendifier = surveysCB
      // `id` is `0..1` on the resource while `isAddendumQuestionnaire` takes a
      // `string`. The assertion preserves the existing call: the helper body is
      // `ADDENDUM_QUESTIONNAIRE_IDS.includes(id)`, and `includes(undefined)`
      // returns `false`, so an absent id is a non-addendum today and stays one.
      .filter((questionnaire: ResourceEntry<'Questionnaire'>) => questionnaire?.resource?.status === 'active' && !isAddendumQuestionnaire(questionnaire?.resource?.id as string))
      .map((option: ResourceEntry<'Questionnaire'>) => {
        const QHelper = new QuestionnaireHelper(option.resource);
        const QExtHelper = new QuestionnaireExtensionHelper(QHelper.getSurveyExtension());
        const obj = {
          key: option.resource.id,
          text: QExtHelper.getInternalTitle() ?? option.resource.title,
          value: option.resource.title,
        };
        return obj;
      })
      /*
       * SUSPECTED DEFECT — this comparator is not a valid one, so the
       * "alphabetical" survey ordering it is meant to produce is not guaranteed.
       *
       * `Array.prototype.sort` requires a comparator returning a negative
       * number, zero, or a positive number. This one returns `-1` when
       * `a.text < b.text` and falls off the end — returning `undefined` — in
       * every other case. `undefined` is not a number; the spec coerces it via
       * `ToNumber` to `NaN`, and a `NaN` comparison result is treated as `0`,
       * i.e. "these two are equal". The comparator therefore never reports that
       * `a` should come AFTER `b`.
       *
       * With V8's TimSort the observable result is implementation-defined and
       * input-order-dependent: it happens to look alphabetical for many inputs,
       * which is why this has survived, but it is not sorted in general.
       *
       * The typed shape also disagreed with the values. The parameters were
       * annotated `Option`, whose `text` is `string`, while the objects being
       * sorted come from `Questionnaire.title`, which FHIR leaves `0..1`. Under
       * the real element type `a.text < b.text` is a comparison that may involve
       * `undefined` on either side.
       *
       * Not fixed. Returning `0`/`1` for the remaining cases would change the
       * emitted order of the survey dropdown — the visible product behaviour
       * this whole pass is required not to touch. The assertion below preserves
       * the fall-off-the-end body exactly; it records that the callback does not
       * satisfy `sort`'s contract rather than making it satisfy it. (It costs one
       * redundant paren pair in the emitted JS, which is the only difference this
       * file shows under a types-erased diff.)
       */
      .sort(((a: QuestionnaireOption, b: QuestionnaireOption) => {
        if (a.text! < b.text!) {
          return -1; // sort surveys alphabetically
        }
      }) as (a: QuestionnaireOption, b: QuestionnaireOption) => number);

    setQuestionnaireTypesCB(addTypesCBPIIIdendifier); // CB dropdown list

    // limit surveys by workflow - MEMBER - does NOT include ones w/o workflow
    const surveysMA = orgSurveys.filter((questionnaire: ResourceEntry<'Questionnaire'>) => {
      if (questionnaire.resource.useContext) {
        let orgMatch = false;
        for (let i = 0; i < questionnaire.resource.useContext.length; i++) {
          if (questionnaire.resource.useContext[i]!.code.code === 'workflow') {
            if (questionnaire.resource.useContext[i]!.valueCodeableConcept!.text!.includes('MEMBER')) {
              orgMatch = true;
            } // else return false;
          }
        }
        return orgMatch;
      } else {
        return false;
      }
    });

    const activeTypesMA = surveysMA
      .filter((questionnaire: ResourceEntry<'Questionnaire'>) => questionnaire?.resource?.status === 'active')
      .map((option: ResourceEntry<'Questionnaire'>) => {
        const QHelper = new QuestionnaireHelper(option.resource);
        const QExtHelper = new QuestionnaireExtensionHelper(QHelper.getSurveyExtension());
        return {
          key: option.resource.id,
          text: QExtHelper.getInternalTitle() ?? option.resource.title,
          value: option.resource.title,
        };
      });

    const typesMA = surveysMA.map((option: ResourceEntry<'Questionnaire'>) => {
      const QHelper = new QuestionnaireHelper(option.resource);
      const QExtHelper = new QuestionnaireExtensionHelper(QHelper.getSurveyExtension());
      return {
        key: option.resource.id,
        text: QExtHelper.getInternalTitle() ?? option.resource.title,
        value: option.resource.title,
      };
    });

    setQuestionnaireTypesMA(activeTypesMA); // MA dropdown list //web survey  what should we do with pii surveys??

    const typesOrg = [{ key: 'Food Rating', text: 'Food Rating', value: 'Food Rating' }, ...typesMA, ...typesCB];
    //filter out duplicates
    const unique = typesOrg.filter((obj, index) => typesOrg.findIndex((item) => item.key === obj.key) === index);
    props.setQuestionnaireTypesOrg(unique); // QR filter

    const typesText = props.questionnaireData.entry
      .filter((questionnaire: ResourceEntry<'Questionnaire'>) => questionnaire.resource.id === 'glucose-text-survey')
      .map((option: ResourceEntry<'Questionnaire'>) => ({
        key: option.resource.id,
        text: option.resource.title,
        value: option.resource.title,
      }));
    setQuestionnaireTypesText(typesText);
  }, [props.org, props.questionnaireData.entry]);

  /*
   * SUSPECTED DEFECT — the survey lookups in this file and the next match on
   * the RENDERED LABEL rather than on the option's key, and then index `[0]`
   * of the result without checking it exists.
   *
   * `e.target.textContent` is the text of the DOM node the user clicked. It is
   * compared against `option.text`, which is `QuestionnaireExtensionHelper
   * .getInternalTitle() ?? Questionnaire.title` — both `0..1` in FHIR. When the
   * matched option's `key` (from `Questionnaire.id`, also `0..1`) is
   * `undefined`, the inner `filter` compares `x.resource.id === undefined` and
   * matches NOTHING, so `[0]` is `undefined` and `.resource` throws
   * `TypeError: Cannot read properties of undefined`, taking down the toolbar
   * on a click.
   *
   * Two surveys sharing a title collapse the same way in the opposite
   * direction: the loop keeps overwriting and the LAST title-match wins, which
   * need not be the survey whose label was clicked.
   *
   * LIVE for any Questionnaire seeded without an `id` or with a duplicate
   * title. Latent for the current seed data, where ids are present and titles
   * happen to be distinct.
   *
   * Not fixed. Both the guard that would prevent the throw and matching on
   * `key` instead of `text` change which survey is opened — or whether one
   * opens at all — and that is the behaviour this pass must leave alone.
   */
  // The three dropdown handlers below match on `e.target.textContent` — the menu item's
  // visible label, not the event's `value`. Cast, not an `instanceof` narrowing: that
  // would yield `null` where this yields `undefined`.
  const onSetDropdown = (e: React.SyntheticEvent<HTMLElement>) => {
    let filteredTableData: Questionnaire | undefined;
    // `for...in` over an array, as upstream writes it. `for...of` emits a different loop.
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const key in questionnaireTypesCB) {
      if (questionnaireTypesCB[key]!.text === (e.target as HTMLElement).textContent) {
        filteredTableData = props.questionnaireData.entry.filter((x: ResourceEntry<'Questionnaire'>) => {
          return x.resource.id === questionnaireTypesCB[key]!.key;
        })[0]!.resource;
        // Kept as an expression statement: an `if` would emit different JavaScript.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        filteredTableData.id && props.handleTranslation(filteredTableData.id)
          ? props.setContainsLang(true)
          : props.setContainsLang(false);
      }
    }
    props.setCurrentQuestionnaire(filteredTableData);
    props.setCurrentQuestionnaireResponse(undefined);
  };

  const handleWebSurvey = (e: React.SyntheticEvent<HTMLElement>) => {
    let filteredTableData: Partial<Questionnaire> = {};
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const key in questionnaireTypesMA) {
      if (questionnaireTypesMA[key]!.text === (e.target as HTMLElement).textContent) {
        filteredTableData = props.questionnaireData.entry.filter((x: ResourceEntry<'Questionnaire'>) => {
          return x.resource.id === questionnaireTypesMA[key]!.key;
        })[0]!.resource;
      }
    }
    webSurvey = filteredTableData;
  };

  const handleWebSurveyMedium = (e: React.SyntheticEvent<HTMLElement>) => {
    webSurveyMedium = (e.target as HTMLElement).textContent;
  };

  const webClick = (s: Partial<Questionnaire> | '', type: string) => {
    setErrorMessage('Oops! Something went wrong..');
    try {
      if (s === '') throw new Error('Unsupported action');
      let body = {};
      switch (type) {
        case 'Email':
          // `!` rather than `?.`: these two props are optional (the card
          // forwards its own optional ones), and indexing an absent one throws
          // TODAY. The assertion erases to nothing and preserves that throw;
          // optional chaining would emit a guard and turn it into a silent
          // "Missing Patient Email" instead.
          if (!props.patientEmail![0]) {
            setErrorMessage('Missing Patient Email');
            throw new Error('Missing Information');
          }
          body = {
            patientId: props.patientId,
            memberName: props.patientFirstName,
            sender: `${authInfo.practitionerName}`,
            email: props.patientEmail,
            emailTemplate: 'd-285bf9513f8a4c6ebe0821a5e39fbbfb',
            subject: s['title'],
            questionnaireId: s['id'],
            text: 'web based survey',
          };
          break;
        case 'Text':
          if (!props.patientPhoneNumber![0]) {
            setErrorMessage('Missing Patient Mobile Phone Number');
            throw new Error('Missing Information');
          }
          /*
           * SUSPECTED DEFECT — the presence check and the value sent disagree
           * about arity.
           *
           * `patientPhoneNumber` is `FlatPatient.mobilePhone`, the raw
           * `fhirpath.evaluate` collection of every `telecom` entry with
           * `system='phone'` and `use='mobile'`. The guard above tests element
           * `[0]`, so it asks "is there at least one mobile number?" — but the
           * value interpolated here is the WHOLE ARRAY, which stringifies by
           * joining on commas.
           *
           * For the one-number case, the common one, `String(['5551234'])` is
           * `'5551234'` and the result is correct — which is why this has not
           * been noticed. For a member with two mobile telecom entries the
           * destination becomes `+15551234,5555678`, a number that cannot
           * receive the survey link. The member is silently not contacted.
           *
           * LIVE, not latent: FHIR `Patient.telecom` is `0..*` and nothing
           * upstream collapses it to one entry.
           *
           * Not fixed here. Taking `[0]` would change the value sent to the
           * `/hooks/text-survey` endpoint for exactly the members currently
           * getting a malformed one, which is a behaviour change and needs a
           * product decision about which number is the right one.
           */
          body = {
            patientId: props.patientId,
            sender: `${authInfo.practitionerName}`,
            // KNOWN_BUGS.md Q-11: `patientPhoneNumber` is the raw fhirpath collection, so
            // this concatenates an array. Cast, not indexed.
            phoneNumber: '+1' + (props.patientPhoneNumber as unknown as string),
            subject:
              s['title'] === 'Terms and Conditions - healthAlign'
                ? 'Please agree to the NourishedRx Terms and Conditions by clicking below:'
                : 'DO NOT REPLY. Please answer your ' + s['title'] + ' survey.', //Added no reply message for members to not text back
            questionnaireId: s['id'],
            emailTitle: s['title'],
            text: 'web based survey',
          };
          break;
        default:
          throw new Error('Unsupported action');
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      HooksPoster<{ message?: string }>(`/hooks/web-survey`, authInfo.token, body).then((data) => {
        console.log(data.message);
      });
      setLaunchStatus(messageStatus.success);
      //probably should move where this cleaning is handled
      webSurveyMedium = '';
      webSurvey = '';
    } catch (e) {
      console.error(e);
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      (e instanceof Error ? e.message : String(e)) === 'Missing Information'
        ? setLaunchStatus(messageStatus.warning)
        : setLaunchStatus(messageStatus.error);
      webSurveyMedium = '';
      webSurvey = '';
    }
  };

  const _textClick = (s: React.SyntheticEvent<HTMLElement>) => {
    setErrorMessage('Oops! Something went wrong..');
    try {
      if (!props.patientPhoneNumber![0]) {
        setErrorMessage('Missing Patient Mobile Phone Number');
        throw new Error('Missing Information');
      }
      let filteredTableData: Partial<Questionnaire> = {};
      // eslint-disable-next-line @typescript-eslint/no-for-in-array
      for (const key in questionnaireTypesMA) {
        if (questionnaireTypesMA[key]!.text === (s.target as HTMLElement).textContent) {
          filteredTableData = props.questionnaireData.entry.filter((x: ResourceEntry<'Questionnaire'>) => {
            return x.resource.id === questionnaireTypesMA[key]!.key;
          })[0]!.resource;
        }
      }
      const body = {
        patientId: props.patientId,
        sender: `${authInfo.practitionerName}`,
        // Same Q-11 array concatenation as the site above.
        phoneNumber: '+1' + (props.patientPhoneNumber as unknown as string),
        subject: filteredTableData.title,
        questionnaireId: filteredTableData.id,
        text: 'web based survey',
      };

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      HooksPoster<{ message?: string }>(`/hooks/text-survey`, authInfo.token, body).then((data) =>
        console.log(data.message)
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <div>
        {!props.questionnaireData && <Loader active inline="centered" />}{' '}
        {props.questionnaireData && (
          <span>
            <Form>
              <Form.Group>
                <div style={{ width: '10px' }} />

                <Dropdown
                  pointing="left"
                  color="blue"
                  icon="write"
                  button
                  className="icon cb_primarybutton"
                  text="Take New Survey &nbsp;"
                >
                  <Dropdown.Menu>
                    <Dropdown.Menu onClick={(e: React.MouseEvent<HTMLDivElement>) => onSetDropdown(e)} scrolling>
                      {questionnaireTypesCB.map((option) => (
                        /*
                         * `key` appears twice: the spread carries it as a DATA
                         * prop as well as React's reserved one. The explicit
                         * attribute is what React consumes, the spread's copy
                         * is inert. Dropping either changes what Semantic
                         * receives.
                         *
                         * Cast because `QuestionnaireOption`'s fields are
                         * `string | undefined` (FHIR `0..1`) while
                         * `DropdownItemProps` declares `text`/`value` without
                         * it. Semantic renders an absent `text` as a blank row;
                         * the cast records that pass rather than substituting a
                         * default.
                         */
                        <Dropdown.Item key={option.key} {...(option as DropdownItemProps)} />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Menu>
                </Dropdown>

                {/* <Dropdown
            pointing="left"
            color="blue"
            icon="mobile alternate"
            button
            className="icon cb_primarybutton"
            text="Launch Text Survey &nbsp;"
            >
              <Dropdown.Menu>
              <Dropdown.Menu onClick={(e) => textClick(e)} scrolling>
                  {questionnaireTypesText.map((option) => (
                    <Dropdown.Item key={option.key} {...option} />
                  ))}
                </Dropdown.Menu>
              </Dropdown.Menu>
            </Dropdown> */}
                {currentEnv !== 'prod' && (
                  <Modal
                    onClose={() => {
                      setLaunchStatus(messageStatus.idle);
                      setOpenWeb(false);
                    }}
                    onOpen={() => {
                      setLaunchStatus(messageStatus.idle);
                      setOpenWeb(true);
                    }}
                    size="large"
                    open={openWeb}
                    closeOnDimmerClick={false}
                    trigger={
                      <Button color="blue" className={props.doNotContact ? 'ui disabled button' : 'false'}>
                        Launch Web Survey &nbsp; <Icon fitted name="laptop" />
                      </Button>
                    }
                  >
                    <Modal.Header>Launch Web Survey</Modal.Header>
                    <Modal.Content scrolling fluid style={{ height: '415px' }}>
                      <Dropdown
                        style={{ margin: '5px' }}
                        placeholder="Choose where to launch web survey &nbsp;"
                        fluid
                        selection
                        options={webSurveyOptions}
                        onChange={(e) => handleWebSurveyMedium(e)}
                      />
                      <Dropdown
                        style={{ margin: '5px' }}
                        placeholder="Choose survey to launch &nbsp;"
                        upward={false}
                        scrolling
                        selection
                        fluid
                        // Same `QuestionnaireOption` vs `DropdownItemProps`
                        // disagreement as the menu above: FHIR `0..1` fields
                        // against Semantic props declared without `| undefined`.
                        options={questionnaireTypesMA as DropdownItemProps[]}
                        onChange={(e) => handleWebSurvey(e)}
                      />
                      {launchStatus === messageStatus.success && (
                        <Message success floating>
                          <Icon size="big" fitted name="check circle" />
                          &nbsp; &nbsp; &nbsp;Survey Launched Succesfully! &nbsp;
                        </Message>
                      )}
                      {launchStatus === messageStatus.warning && (
                        <Message warning floating>
                          <Icon size="big" fitted name="exclamation triangle" />
                          &nbsp; &nbsp; {`${errorMessage}`} &nbsp;
                        </Message>
                      )}
                      {launchStatus === messageStatus.error && (
                        <Message negative floating>
                          <Icon size="big" fitted name="exclamation circle" />
                          &nbsp; &nbsp; {`${errorMessage}`} &nbsp;
                        </Message>
                      )}
                    </Modal.Content>
                    <Modal.Actions>
                      <Button
                        content="Cancel"
                        onClick={() => {
                          setOpenWeb(false);
                        }}
                      />
                      <Button
                        color="blue"
                        labelPosition="right"
                        icon="paper plane"
                        content="Send"
                        onClick={() => {
                          // SUSPECTED DEFECT — REPORTED, NOT FIXED. `webSurvey` is
                          // declared uninitialised (as upstream declares it), so before
                          // any survey is chosen it is `undefined`, not `''`. `webClick`
                          // guards only `s === ''`, so the guard misses and `s['title']`
                          // throws. Cast rather than guarded: adding the `undefined` case
                          // would turn today's throw into a silent no-op.
                          webClick(webSurvey as Partial<Questionnaire> | '', webSurveyMedium); /*setOpenWeb(false)*/
                        }}
                      />
                    </Modal.Actions>
                  </Modal>
                )}
                {/*
                  `value` and `onChange` are both passed as possibly-`undefined`
                  — `language` is the card's `surveyLanguage` state and
                  `handleLanguageSelect` is optional on this contract — while
                  `DropdownProps` declares neither with `| undefined`. Under
                  `exactOptionalPropertyTypes` a pass of `undefined` is not an
                  omission, so each is asserted at the attribute. Defaulting
                  either would change what Semantic receives.
                */}
                <Dropdown
                  control={'select'}
                  style={{ marginLeft: '1%', maxHeight: '3px' }}
                  scrolling
                  selection
                  placeholder={!props.containsLang ? 'English (US)' : 'Language'}
                  disabled={!props.containsLang}
                  compact
                  options={props.langsList}
                  value={(!props.containsLang ? 'English (US)' : props.language) as string}
                  onChange={props.handleLanguageSelect as NonNullable<DropdownProps['onChange']>}
                />
              </Form.Group>
            </Form>
          </span>
        )}
      </div>
    </>
  );
}
